import React, { useEffect, useState, useMemo } from 'react';
import { useI18n } from '../context/I18nContext';
import { publicApi } from '../api';
import { staticImg, getYoutubeId, PLACEHOLDER } from '../utils/media';
import { Maximize2, Play, X, ChevronLeft, ChevronRight, Sparkles, ArrowRight, Video, Image as ImageIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function GallerySection() {
  const { tr, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      try {
        const galRes = await publicApi.gallery(lang).catch(() => []);
        if (isMounted) {
          if (Array.isArray(galRes) && galRes.length > 0) {
            const formatted = galRes.map(g => {
              const videoUrl = g.video_url || g.videoUrl || g.url || '';
              const ytId = getYoutubeId(videoUrl) || getYoutubeId(g.image);
              const isVideo = g.media_type === 'video' || Boolean(videoUrl) || Boolean(ytId);
              
              let thumb = g.image || '';
              if (!thumb && ytId) {
                thumb = `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
              }

              return {
                id: g.id || g._id,
                title: g.title || g.name || 'Aegis Studios Galeri',
                category: g.category || 'systems',
                media_type: isVideo ? 'video' : 'image',
                video_url: videoUrl,
                youtube_id: ytId,
                image: thumb,
                description: g.description || ''
              };
            });
            setItems(formatted);
          } else {
            setItems([]);
          }
        }
      } catch (err) {
        console.error('Gallery fetch error:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadData();
    return () => { isMounted = false; };
  }, [lang]);

  const categories = useMemo(() => [
    { id: 'all', label: tr('gallery.cat_all', 'Tümü') },
    { id: 'files', label: tr('gallery.cat_files', 'Server Files') },
    { id: 'models', label: tr('gallery.cat_models', '3D Modeller') },
    { id: 'systems', label: tr('gallery.cat_systems', 'Sistemler') },
    { id: 'maps', label: tr('gallery.cat_maps', 'Harita & Zindan') },
  ], [tr]);

  const filteredItems = useMemo(() => {
    if (activeCategory === 'all') return items;
    return items.filter(item => item.category === activeCategory);
  }, [items, activeCategory]);

  const openLightbox = (item, idx) => {
    setSelectedItem(item);
    setSelectedIdx(idx);
  };

  const nextItem = (e) => {
    e.stopPropagation();
    if (!filteredItems.length) return;
    const nextIndex = (selectedIdx + 1) % filteredItems.length;
    setSelectedIdx(nextIndex);
    setSelectedItem(filteredItems[nextIndex]);
  };

  const prevItem = (e) => {
    e.stopPropagation();
    if (!filteredItems.length) return;
    const prevIndex = (selectedIdx - 1 + filteredItems.length) % filteredItems.length;
    setSelectedIdx(prevIndex);
    setSelectedItem(filteredItems[prevIndex]);
  };

  if (loading || items.length === 0) return null;

  return (
    <section className="relative py-24 bg-[#08080b] overflow-hidden">
      {/* Background Ambient Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-gradient-to-tr from-[#f4d896]/10 via-[#e4b44e]/5 to-transparent blur-[140px] pointer-events-none rounded-full" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#f4d896]/10 border border-[#f4d896]/20 text-[#f4d896] text-xs font-bold tracking-widest uppercase mb-4 shadow-[0_0_20px_rgba(244,216,150,0.15)]">
              <Sparkles className="w-3.5 h-3.5" />
              {tr('gallery.eyebrow', 'GALERİ & ÖNİZLEMELER')}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white">
              {tr('gallery.title_part1', 'Oyun İçi')} <span className="gold-text-animated">{tr('gallery.title_part2', 'Görseller & Videolar')}</span>
            </h2>
            <p className="mt-3 text-base text-white/60 max-w-xl leading-relaxed">
              {tr('gallery.subtitle', 'Geliştirdiğimiz Metin2 server files paketlerinden, 3d modellerden ve özel haritalardan öne çıkan fotoğraf ve videolar.')}
            </p>
          </div>

          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 hover:border-[#f4d896]/40 hover:bg-[#f4d896]/10 text-white font-extrabold text-xs tracking-widest uppercase transition-all duration-300 group shrink-0"
          >
            <span>{tr('gallery.view_all', 'TÜM GALERİYİ İNCELE')}</span>
            <ArrowRight className="w-4 h-4 text-[#f4d896] transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>

        {/* Category Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 scrollbar-none">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-xl text-xs font-bold tracking-wider uppercase transition-all duration-300 whitespace-nowrap border ${
                activeCategory === cat.id
                  ? 'bg-gradient-to-r from-[#f4d896] to-[#c99a36] text-[#08080b] border-transparent shadow-[0_0_25px_rgba(244,216,150,0.4)]'
                  : 'bg-white/5 border-white/10 text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {filteredItems.map((item, idx) => (
            <div
              key={item.id || idx}
              onClick={() => openLightbox(item, idx)}
              className="group relative h-64 rounded-2xl overflow-hidden border border-white/10 bg-[#0d0d13] cursor-pointer transition-all duration-500 hover:border-[#f4d896]/50 hover:shadow-[0_10px_40px_rgba(0,0,0,0.8),0_0_30px_rgba(244,216,150,0.15)]"
            >
              {/* Image / Thumbnail */}
              <img
                src={item.image ? staticImg(item.image) : (item.youtube_id ? `https://img.youtube.com/vi/${item.youtube_id}/hqdefault.jpg` : PLACEHOLDER)}
                alt={item.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                loading="lazy"
              />

              {/* Overlay Gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080b] via-[#08080b]/40 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-300" />

              {/* Video Play Icon Badge */}
              {item.media_type === 'video' ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-red-600/90 text-white border border-red-400/50 flex items-center justify-center shadow-[0_0_25px_rgba(220,38,38,0.6)] group-hover:scale-110 transition-transform duration-300">
                    <Play className="w-7 h-7 fill-current translate-x-0.5" />
                  </div>
                </div>
              ) : (
                <div className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-black/60 backdrop-blur-md border border-white/20 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 group-hover:scale-100 scale-75 transition-all duration-300 hover:bg-[#f4d896] hover:text-[#08080b]">
                  <Maximize2 className="w-4 h-4" />
                </div>
              )}

              {/* Content Box */}
              <div className="absolute bottom-0 inset-x-0 p-5 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-block px-2.5 py-1 rounded-md bg-[#f4d896]/20 border border-[#f4d896]/30 text-[#f4d896] text-[10px] font-black tracking-widest uppercase">
                    {item.category === 'files' ? 'Server Files' : item.category === 'models' ? '3D Model' : item.category === 'maps' ? 'Harita' : 'Sistem'}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase ${item.media_type === 'video' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'}`}>
                    {item.media_type === 'video' ? <Video className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                    {item.media_type === 'video' ? 'Video' : 'Fotoğraf'}
                  </span>
                </div>
                <h3 className="font-display text-base font-bold text-white group-hover:text-[#f4d896] transition-colors duration-300 line-clamp-1">
                  {item.title}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox / Video Modal */}
      {selectedItem && (
        <div
          className="fixed inset-0 z-[999] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 sm:p-8 animate-fadeIn"
          onClick={() => setSelectedItem(null)}
        >
          {/* Close Button */}
          <button
            onClick={() => setSelectedItem(null)}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-red-500 hover:border-red-500 transition-all duration-300 z-10"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Previous Button */}
          <button
            onClick={prevItem}
            className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-[#f4d896] hover:text-[#08080b] transition-all duration-300 z-10"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          {/* Next Button */}
          <button
            onClick={nextItem}
            className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-[#f4d896] hover:text-[#08080b] transition-all duration-300 z-10"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Modal Content */}
          <div
            className="relative w-full max-w-5xl max-h-[85vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedItem.media_type === 'video' && selectedItem.youtube_id ? (
              <div className="w-full aspect-video rounded-2xl overflow-hidden border border-white/15 shadow-[0_0_60px_rgba(0,0,0,0.9)] bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${selectedItem.youtube_id}?autoplay=1`}
                  title={selectedItem.title}
                  className="w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <img
                src={selectedItem.image ? staticImg(selectedItem.image) : (selectedItem.youtube_id ? `https://img.youtube.com/vi/${selectedItem.youtube_id}/hqdefault.jpg` : PLACEHOLDER)}
                alt={selectedItem.title}
                className="max-w-full max-h-[75vh] object-contain rounded-2xl border border-white/15 shadow-[0_0_60px_rgba(0,0,0,0.9)]"
              />
            )}
            <div className="mt-4 text-center">
              <h3 className="font-display text-xl font-bold text-white mb-1">{selectedItem.title}</h3>
              {selectedItem.description && (
                <p className="text-sm text-white/60 max-w-lg">{selectedItem.description}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

